import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../lib/auth'

/**
 * Shown while the owner is in "View as" (User Management ⋯ menu). The pages
 * and buttons are the viewed user's; the server refuses every change made with
 * this token. Exit (or the 30-minute expiry) returns to the owner's account.
 */
export function ViewAsBanner() {
  const { user, viewingAs, stopViewAs } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))

  useEffect(() => {
    if (!viewingAs) return
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 5_000)
    return () => clearInterval(t)
  }, [viewingAs])

  // Exit a little before the token expires, so the owner returns to their own
  // account cleanly instead of hitting a 401 and the login screen.
  const expired = viewingAs && !!user && user.exp - 20 <= now

  async function exit() {
    await stopViewAs()
    qc.clear()                 // cached data was fetched as the viewed user
    navigate('/users')
  }

  useEffect(() => {
    if (expired) void exit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expired])

  if (!viewingAs || !user) return null
  const minutesLeft = Math.max(0, Math.ceil((user.exp - now) / 60))

  return (
    <div className="sticky top-0 z-50 bg-honey-500 text-ink-900 px-4 py-2 flex items-center gap-3 text-[13px]">
      <span className="font-semibold">Viewing as {user.name} ({user.email}) — read-only</span>
      <span className="text-ink-800">You see what they can see; nothing can be changed. Ends in {minutesLeft} min.</span>
      <div className="flex-1" />
      <button onClick={exit}
        className="h-7 px-3 rounded-md bg-ink-900 text-white font-semibold text-[12.5px] hover:bg-ink-700">
        Exit
      </button>
    </div>
  )
}
